/**
 * A- / A+ in the app bar.
 *
 * The text scale already existed in Personalization, but a setting three
 * clicks into a dialog is not one a desk operator adjusts mid-shift. This puts
 * it where the complaint happens: one press, applied instantly, for whoever is
 * sitting there now.
 *
 * Deliberately LETTERS, not a magnifier icon. A magnifier means "search"
 * everywhere else in this app — including in the button immediately to the
 * left of these — and two meanings for one glyph in one toolbar is how an
 * operator learns to distrust the toolbar.
 *
 * The buttons disable at the ends rather than wrapping, and the disabled state
 * is what tells the operator they are already at the largest — an unchanged
 * screen after a press otherwise reads as "the button is broken".
 */
import type { JSX } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  usePreferencesStore,
  stepTextSize,
  TEXT_SIZE_STEPS,
  TEXT_SIZE_LABEL,
} from '@store/preferences.store';

export function TextSizeControl(): JSX.Element {
  const textSize = usePreferencesStore((state) => state.textSize);
  const setTextSize = usePreferencesStore((state) => state.setTextSize);

  const atSmallest = textSize === TEXT_SIZE_STEPS[0];
  const atLargest = textSize === TEXT_SIZE_STEPS[TEXT_SIZE_STEPS.length - 1];

  // `fontSize` is fixed in px on purpose: these two controls must not grow with
  // the very scale they set, or pressing A+ walks them across the toolbar.
  const glyph = (text: string, size: number) => (
    <Typography component="span" sx={{ fontSize: `${size}px`, fontWeight: 700, lineHeight: 1 }}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip describeChild title={atSmallest ? `Text size: ${TEXT_SIZE_LABEL[textSize]} (smallest)` : 'Decrease text size'}>
        {/* A disabled button fires no events, so the tooltip needs a real
            element to hang from — hence the wrapper span. `describeChild` is
            what stops MUI copying the tooltip title onto that span as a second
            `aria-label`: the button is already named, and two labelled nodes
            for one control is a duplicate announcement to a screen reader. */}
        <span>
          <IconButton
            aria-label="Decrease text size"
            size="small"
            disabled={atSmallest}
            onClick={() => setTextSize(stepTextSize(textSize, -1))}
          >
            {glyph('A−', 12)}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip describeChild title={atLargest ? `Text size: ${TEXT_SIZE_LABEL[textSize]} (largest)` : 'Increase text size'}>
        <span>
          <IconButton
            aria-label="Increase text size"
            size="small"
            disabled={atLargest}
            onClick={() => setTextSize(stepTextSize(textSize, 1))}
          >
            {glyph('A+', 16)}
          </IconButton>
        </span>
      </Tooltip>

      {/* Announced, not shown: the visible change IS the feedback for sighted
          users, but a screen-reader user gets nothing from a font-size change. */}
      <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }} aria-live="polite">
        {`Text size ${TEXT_SIZE_LABEL[textSize]}`}
      </Box>
    </Box>
  );
}
